// Popups API client (ENT-08, config-tier) — düz popup (tarih-aktif + frekans).
//
// Uçlar `/api/v1/popups/...`, `web.manage` policy'si. Publish iki-durum toggle.
// İş kuralları BE'de: INVALID_FREQUENCY / LANGUAGE_INACTIVE.

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import {
  normalizeConfigStatus,
  type CreatePopupRequest,
  type Popup,
  type PopupDtoWire,
  type PopupInput,
  type PopupListItem,
  type PopupListItemDtoWire,
  type UpdatePopupRequest,
} from '@features/widgets/types';

const BASE = '/api/v1/popups';

interface PopupListQuery {
  languageId?: string;
}

function mapListItem(d: PopupListItemDtoWire): PopupListItem {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    frequency: d.frequency ?? '',
    startDate: d.startDate ?? null,
    endDate: d.endDate ?? null,
    updatedAt: d.updatedAt ?? '',
  };
}

function mapPopup(d: PopupDtoWire): Popup {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    imageUrl: d.imageUrl ?? null,
    title: d.title ?? null,
    body: d.body ?? null,
    ctaText: d.ctaText ?? null,
    ctaLink: d.ctaLink ?? null,
    targetPages: d.targetPages ?? null,
    frequency: d.frequency ?? '',
    startDate: d.startDate ?? null,
    endDate: d.endDate ?? null,
    variantLanguageCodes: d.variantLanguageCodes ?? [],
  };
}

function toRequestBody(input: PopupInput): CreatePopupRequest {
  return {
    name: input.name,
    frequency: input.frequency,
    imageUrl: input.imageUrl ?? null,
    title: input.title ?? null,
    body: input.body ?? null,
    ctaText: input.ctaText ?? null,
    ctaLink: input.ctaLink ?? null,
    targetPages: input.targetPages ?? null,
    startDate: input.startDate ?? null,
    endDate: input.endDate ?? null,
  };
}

export const popupsApi = {
  async list(query: PopupListQuery = {}): Promise<PopupListItem[]> {
    const params: Record<string, string | number> = { page: 1, pageSize: 200 };
    if (query.languageId) params.languageId = query.languageId;
    const res = await http.get<ActionResponse<PopupListItemDtoWire>>(BASE, { params });
    return unwrapList(res).items.map(mapListItem);
  },

  async get(id: string): Promise<Popup> {
    return mapPopup(unwrap(await http.get<ActionResponse<PopupDtoWire>>(`${BASE}/${id}`)));
  },

  async create(input: PopupInput): Promise<Popup> {
    const body: CreatePopupRequest = {
      ...toRequestBody(input),
      ...(input.languageId ? { languageId: input.languageId } : {}),
    };
    return mapPopup(unwrap(await http.post<ActionResponse<PopupDtoWire>>(BASE, body)));
  },

  async update(id: string, input: PopupInput): Promise<Popup> {
    const body: UpdatePopupRequest = toRequestBody(input);
    unwrap(await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body));
    return popupsApi.get(id);
  },

  async remove(id: string): Promise<void> {
    unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`));
  },

  async setPublished(id: string, publish: boolean): Promise<void> {
    unwrap(
      await http.post<ActionResponse<boolean>>(`${BASE}/${id}/publish`, null, {
        params: { publish },
      }),
    );
  },
};
